import { DatePickerFr } from '../ui/DatePickerFr';
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';

import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart as RechartsPieChart, Pie, Cell, Legend, AreaChart, Area } from 'recharts';
import { Banknote, BarChart3, CalendarDays, CheckCircle, Clock, CreditCard, Download, Euro, FileText, Landmark, Loader2, PieChart, RefreshCw, Target, TrendingUp, Wallet } from 'lucide-react';
import { formatMoney } from './constants';
import { PaymentLinkSection } from '../PaymentLinkSection';
import ComptabiliteCharts from './ComptabiliteCharts';

export default function ComptabiliteTab({
  comptaDateDebut,
  comptaDateFin,
  comptaPaymentRef,
  comptaStats,
  exportLoading,
  fetchComptaStats,
  filtered,
  getAmount,
  getAuthHeader,
  handleExport,
  handleExportExcelComplet,
  isPaid,
  loadingStats,
  setComptaDateDebut,
  setComptaDateFin,
  setComptaPaymentRef,
  setPeriode,
  stats: stats
}) {
  return (
    <>
          {/* Header avec filtres de période */}
          <Card className="bg-gradient-to-r from-emerald-50 to-teal-50 border-emerald-200">
            <CardContent className="py-4">
              <div className="flex flex-wrap items-center gap-4">
                <div className="flex items-center gap-2">
                  <PieChart className="h-5 w-5 text-emerald-600" />
                  <h3 className="font-bold text-emerald-800">Tableau de Bord Comptable</h3>
                </div>
                
                <div className="flex items-center gap-2 ml-auto">
                  <Label className="text-sm text-emerald-700">Période:</Label>
                  <DatePickerFr
                    value={comptaDateDebut}
                    onChange={setComptaDateDebut}
                    className="w-36 text-sm"
                  />
                  <span className="text-emerald-600">→</span>
                  <DatePickerFr
                    value={comptaDateFin}
                    onChange={setComptaDateFin}
                    className="w-36 text-sm"
                  />
                </div>
                
                <div className="flex gap-1">
                  <Button size="sm" variant="outline" onClick={() => setPeriode('mois')} className="text-xs">
                    Ce mois
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setPeriode('trimestre')} className="text-xs">
                    Ce trimestre
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setPeriode('annee')} className="text-xs">
                    Cette année
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setPeriode('tout')} className="text-xs">
                    Tout
                  </Button>
                </div>
                
                <Button onClick={fetchComptaStats} disabled={loadingStats} size="sm" className="bg-emerald-600 hover:bg-emerald-700">
                  {loadingStats ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                  <span className="ml-1">Actualiser</span>
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Stats principales */}
          {comptaStats && (
            <>
              {/* KPIs principaux */}
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white">
                  <CardContent className="pt-4 pb-3">
                    <Target className="h-5 w-5 mb-1 opacity-70" />
                    <p className="text-2xl font-bold">{comptaStats.souscriptions?.total || 0}</p>
                    <p className="text-xs opacity-80">Souscriptions</p>
                  </CardContent>
                </Card>
                
                <Card className="bg-gradient-to-br from-green-500 to-emerald-600 text-white">
                  <CardContent className="pt-4 pb-3">
                    <CheckCircle className="h-5 w-5 mb-1 opacity-70" />
                    <p className="text-2xl font-bold">{comptaStats.souscriptions?.acquittees || 0}</p>
                    <p className="text-xs opacity-80">Acquittées</p>
                  </CardContent>
                </Card>
                
                <Card className="bg-gradient-to-br from-amber-500 to-orange-500 text-white">
                  <CardContent className="pt-4 pb-3">
                    <Clock className="h-5 w-5 mb-1 opacity-70" />
                    <p className="text-2xl font-bold">{comptaStats.souscriptions?.en_attente || 0}</p>
                    <p className="text-xs opacity-80">En attente</p>
                  </CardContent>
                </Card>
                
                <Card className="bg-gradient-to-br from-purple-500 to-indigo-600 text-white">
                  <CardContent className="pt-4 pb-3">
                    <Euro className="h-5 w-5 mb-1 opacity-70" />
                    <p className="text-xl font-bold">{formatMoney(comptaStats.montants?.total || 0)}</p>
                    <p className="text-xs opacity-80">Total facturé</p>
                  </CardContent>
                </Card>
                
                <Card className="bg-gradient-to-br from-teal-500 to-cyan-600 text-white">
                  <CardContent className="pt-4 pb-3">
                    <Banknote className="h-5 w-5 mb-1 opacity-70" />
                    <p className="text-xl font-bold">{formatMoney(comptaStats.montants?.encaisse || 0)}</p>
                    <p className="text-xs opacity-80">Encaissé</p>
                  </CardContent>
                </Card>
                
                <Card className="bg-gradient-to-br from-rose-500 to-pink-600 text-white">
                  <CardContent className="pt-4 pb-3">
                    <Target className="h-5 w-5 mb-1 opacity-70" />
                    <p className="text-2xl font-bold">{comptaStats.montants?.taux_recouvrement || 0}%</p>
                    <p className="text-xs opacity-80">Recouvrement</p>
                  </CardContent>
                </Card>
              </div>

              {/* Statistiques détaillées */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Paiements par mode */}
                <Card>
                  <CardContent className="pt-4">
                    <h4 className="font-semibold text-gray-700 mb-3 flex items-center gap-2">
                      <CreditCard className="h-4 w-4 text-indigo-500" />
                      Paiements par Mode
                    </h4>
                    <div className="space-y-2">
                      {Object.entries(comptaStats.paiements_par_mode || {}).map(([mode, data]) => (
                        <div key={mode} className="flex justify-between items-center text-sm">
                          <span className="capitalize flex items-center gap-2">
                            {mode === 'virement' && '💳'}
                            {mode === 'sepa' && '🏦'}
                            {mode === 'carte' && '💳'}
                            {mode === 'cheque' && '📝'}
                            {mode === 'especes' && '💵'}
                            {mode}
                          </span>
                          <div className="text-right">
                            <span className="font-semibold">{data.count}</span>
                            <span className="text-gray-400 ml-2">({formatMoney(data.montant)})</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {/* SEPA Core / B2B */}
                <Card>
                  <CardContent className="pt-4">
                    <h4 className="font-semibold text-gray-700 mb-3 flex items-center gap-2">
                      <Landmark className="h-4 w-4 text-blue-500" />
                      Mandats SEPA
                    </h4>
                    <div className="space-y-3">
                      <div className="bg-blue-50 p-3 rounded-lg">
                        <div className="flex justify-between">
                          <span className="text-blue-700 font-medium">SEPA Core</span>
                          <Badge className="bg-blue-700 text-white font-semibold">{comptaStats.sepa?.core?.count || 0}</Badge>
                        </div>
                        <p className="text-lg font-bold text-blue-800 mt-1">{formatMoney(comptaStats.sepa?.core?.montant || 0)}</p>
                        <p className="text-xs text-blue-600">Particuliers - 14j notification</p>
                      </div>
                      <div className="bg-purple-50 p-3 rounded-lg">
                        <div className="flex justify-between">
                          <span className="text-purple-700 font-medium">SEPA B2B</span>
                          <Badge className="bg-purple-700 text-white font-semibold">{comptaStats.sepa?.b2b?.count || 0}</Badge>
                        </div>
                        <p className="text-lg font-bold text-purple-800 mt-1">{formatMoney(comptaStats.sepa?.b2b?.montant || 0)}</p>
                        <p className="text-xs text-purple-600">Entreprises - 1j notification</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Acomptes & Échéances */}
                <Card>
                  <CardContent className="pt-4">
                    <h4 className="font-semibold text-gray-700 mb-3 flex items-center gap-2">
                      <Wallet className="h-4 w-4 text-amber-500" />
                      Acomptes & Échéances
                    </h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span>Acomptes reçus</span>
                        <span className="font-semibold text-green-600">{comptaStats.acomptes?.recus || 0} ({formatMoney(comptaStats.acomptes?.montant_recus || 0)})</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Acomptes programmés</span>
                        <span className="font-semibold text-blue-600">{comptaStats.acomptes?.programmes || 0} ({formatMoney(comptaStats.acomptes?.montant_programmes || 0)})</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Acomptes annulés</span>
                        <span className="font-semibold text-red-600">{comptaStats.acomptes?.annules || 0}</span>
                      </div>
                      <hr className="my-2" />
                      <div className="flex justify-between">
                        <span>Échéances payées</span>
                        <span className="font-semibold text-green-600">{comptaStats.echeances?.payees || 0}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Échéances en attente</span>
                        <span className="font-semibold text-amber-600">{comptaStats.echeances?.en_attente || 0}</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              <ComptabiliteCharts
                comptaStats={comptaStats}
                filtered={filtered}
                getAmount={getAmount}
                isPaid={isPaid}
              />
            </>
          )}

          {/* Exports */}
          <Card className="border-2 border-emerald-200">
            <CardContent className="pt-4">
              <h4 className="font-bold text-gray-800 mb-4 flex items-center gap-2">
                <Download className="h-5 w-5 text-emerald-600" />
                Exports Comptables
              </h4>
              
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                {/* Export Acomptes */}
                <Button 
                  variant="outline" 
                  className="flex flex-col h-auto py-3 border-amber-200 hover:bg-amber-50"
                  onClick={() => handleExport('acomptes', 'csv')}
                  disabled={exportLoading.acomptes}
                >
                  {exportLoading.acomptes ? <Loader2 className="h-5 w-5 animate-spin text-amber-600" /> : <Wallet className="h-5 w-5 text-amber-600" />}
                  <span className="text-xs mt-1">Acomptes</span>
                  <span className="text-[10px] text-gray-400">CSV</span>
                </Button>

                {/* Export Échéanciers */}
                <Button 
                  variant="outline" 
                  className="flex flex-col h-auto py-3 border-blue-200 hover:bg-blue-50"
                  onClick={() => handleExport('echeanciers', 'csv')}
                  disabled={exportLoading.echeanciers}
                >
                  {exportLoading.echeanciers ? <Loader2 className="h-5 w-5 animate-spin text-blue-600" /> : <CalendarDays className="h-5 w-5 text-blue-600" />}
                  <span className="text-xs mt-1">Échéanciers</span>
                  <span className="text-[10px] text-gray-400">CSV</span>
                </Button>

                {/* Export Contributions Acquittées */}
                <Button 
                  variant="outline" 
                  className="flex flex-col h-auto py-3 border-green-200 hover:bg-green-50"
                  onClick={() => handleExport('contributions-acquittees', 'csv')}
                  disabled={exportLoading['contributions-acquittees']}
                >
                  {exportLoading['contributions-acquittees'] ? <Loader2 className="h-5 w-5 animate-spin text-green-600" /> : <CheckCircle className="h-5 w-5 text-green-600" />}
                  <span className="text-xs mt-1">Acquittées</span>
                  <span className="text-[10px] text-gray-400">CSV</span>
                </Button>

                {/* Export par Mode */}
                <Button 
                  variant="outline" 
                  className="flex flex-col h-auto py-3 border-indigo-200 hover:bg-indigo-50"
                  onClick={() => handleExport('paiements-par-mode', 'csv')}
                  disabled={exportLoading['paiements-par-mode']}
                >
                  {exportLoading['paiements-par-mode'] ? <Loader2 className="h-5 w-5 animate-spin text-indigo-600" /> : <CreditCard className="h-5 w-5 text-indigo-600" />}
                  <span className="text-xs mt-1">Par Mode</span>
                  <span className="text-[10px] text-gray-400">CSV</span>
                </Button>

                {/* Export SEPA */}
                <Button 
                  variant="outline" 
                  className="flex flex-col h-auto py-3 border-purple-200 hover:bg-purple-50"
                  onClick={() => handleExport('sepa', 'csv')}
                  disabled={exportLoading.sepa}
                >
                  {exportLoading.sepa ? <Loader2 className="h-5 w-5 animate-spin text-purple-600" /> : <Landmark className="h-5 w-5 text-purple-600" />}
                  <span className="text-xs mt-1">SEPA</span>
                  <span className="text-[10px] text-gray-400">CSV</span>
                </Button>

                {/* Export Excel Complet */}
                <Button 
                  className="flex flex-col h-auto py-3 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white"
                  onClick={handleExportExcelComplet}
                  disabled={exportLoading.excel}
                >
                  {exportLoading.excel ? <Loader2 className="h-5 w-5 animate-spin" /> : <FileText className="h-5 w-5" />}
                  <span className="text-xs mt-1 font-semibold">EXCEL COMPLET</span>
                  <span className="text-[10px] opacity-80">Multi-onglets</span>
                </Button>
              </div>

              <p className="text-xs text-gray-500 mt-4">
                💡 L'export Excel complet contient 5 onglets : Synthèse, Contributions Acquittées, Acomptes, Échéanciers, SEPA Core/B2B
              </p>
            </CardContent>
          </Card>

          {/* Message si pas de stats */}
          {!comptaStats && !loadingStats && (
            <Card className="bg-gray-50">
              <CardContent className="py-12 text-center">
                <PieChart className="h-16 w-16 mx-auto mb-4 text-gray-300" />
                <p className="text-gray-500">Cliquez sur "Actualiser" pour charger les statistiques</p>
              </CardContent>
            </Card>
          )}

          {loadingStats && (
            <Card className="bg-gray-50">
              <CardContent className="py-12 text-center">
                <Loader2 className="h-12 w-12 mx-auto mb-4 animate-spin text-emerald-500" />
                <p className="text-gray-500">Chargement des statistiques...</p>
              </CardContent>
            </Card>
          )}

          {/* Section Lien de Paiement Stripe */}
          <Card className="border-emerald-200">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <CreditCard className="h-5 w-5 text-emerald-600" />
                Lien de paiement Stripe
              </CardTitle>
              <CardDescription>
                Creez ou saisissez un lien de paiement Stripe pour envoyer a un client par email
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div>
                  <Label className="text-xs text-gray-500">Reference de la souscription (optionnel)</Label>
                  <Input
                    data-testid="compta-payment-ref"
                    value={comptaPaymentRef || ''}
                    onChange={(e) => setComptaPaymentRef(e.target.value)}
                    placeholder="Ex: DOM-20260101-ABC123"
                    className="text-sm"
                  />
                </div>
                <PaymentLinkSection
                  reference={comptaPaymentRef || 'COMPTA-MANUAL'}
                  amount={0}
                  customerEmail=""
                  customerName=""
                  source="domium"
                  description={comptaPaymentRef ? `Contribution DOMIUM - ${comptaPaymentRef}` : 'Paiement DOMIUM'}
                  authHeader={getAuthHeader()}
                />
              </div>
            </CardContent>
          </Card>
    </>
  );
}
